/**
 * EbookShell — shared chrome + premium dark cyan/emerald CSS used by both
 * RVTravelGuideEbook and RVBuyingGuideEbook (and any future ebooks).
 *
 * Provides:
 *   - VIP-token gating against /api/ebook/<type>
 *   - Sticky toolbar with Home + Download/Save-as-PDF buttons
 *   - Single global stylesheet scoped to .ebook-shell-root
 *   - Print-friendly layout (toolbar hidden, full-bleed pages)
 *   - Page elements (.cover, .page, .card, .image-box, .cta, .footer, etc.)
 *     so child ebooks just supply <section className="page">…</section>.
 *
 * Variants:
 *   - variant="travel" → mountains + road + green-tinted image-boxes
 *   - variant="buying" → dollar-grid floor + cyan/blue image-boxes (default)
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft } from 'lucide-react';
import BrandLogo from './BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EbookShell = ({ ebookType, variant = 'buying', testIdPrefix = 'ebook', children }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [authorized, setAuthorized] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let active = true;
    if (!token) {
      setAuthorized(false);
      setErrorMsg('VIP access token required.');
      return;
    }
    axios.get(`${API}/ebook/${ebookType}`, { params: { token } })
      .then(() => { if (active) setAuthorized(true); })
      .catch((err) => {
        if (!active) return;
        setAuthorized(false);
        setErrorMsg(err.response?.data?.detail || 'VIP access required to view this ebook.');
      });
    return () => { active = false; };
  }, [token, ebookType]);

  if (authorized === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08111f] text-white">
        <Loader2 className="animate-spin text-cyan-400" size={48} />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08111f] text-white p-6">
        <div className="max-w-md p-8 rounded-2xl bg-[#0b1b2e] border border-cyan-500/20 text-center">
          <p className="text-rose-400 mb-4 text-sm font-semibold uppercase tracking-wider">{errorMsg || 'Access denied'}</p>
          <p className="mb-6 text-gray-300">This ebook is exclusive to VIP members.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-white font-bold text-sm">
            <ArrowLeft size={14} /> Back to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{EBOOK_SHELL_CSS}</style>
      <div className={`ebook-shell-root variant-${variant}`}>
        <div className="toolbar" data-testid={`${testIdPrefix}-toolbar`}>
          <Link
            to="/"
            className="back-btn"
            data-testid={`${testIdPrefix}-home-btn`}
          >
            ← Home
          </Link>
          <div className="ebook-toolbar-logo">
            <BrandLogo size="sm" variant="wordmark" />
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid={`${testIdPrefix}-download-btn`}
          >
            Download / Save as PDF
          </button>
        </div>

        <div className="ebook" data-testid={`${testIdPrefix}-ebook`}>
          {children}
        </div>
      </div>
    </>
  );
};

const EBOOK_SHELL_CSS = `
  .ebook-shell-root, .ebook-shell-root * { box-sizing: border-box; }
  .ebook-shell-root {
    margin: 0;
    font-family: Arial, sans-serif;
    background: #08111f;
    color: white;
    min-height: 100vh;
  }
  .ebook-shell-root .toolbar {
    position: sticky;
    top: 0;
    z-index: 100;
    background: #050b14;
    padding: 12px 16px;
    text-align: center;
    border-bottom: 1px solid rgba(255,255,255,.15);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .ebook-shell-root .back-btn {
    color: rgba(255,255,255,.85);
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.15);
    padding: 10px 16px;
    border-radius: 999px;
    font-size: 13px;
    text-decoration: none;
    font-weight: 600;
  }
  .ebook-shell-root .back-btn:hover { background: rgba(255,255,255,.14); }
  .ebook-shell-root .ebook-toolbar-logo {
    display: inline-flex;
    align-items: center;
    margin: 0 4px;
    opacity: 0.95;
  }
  @media (max-width: 520px) { .ebook-shell-root .ebook-toolbar-logo { display: none; } }
  .ebook-shell-root button {
    background: linear-gradient(135deg, #00d4ff, #2e8b57);
    border: none;
    color: white;
    padding: 12px 22px;
    font-size: 14px;
    font-weight: bold;
    border-radius: 999px;
    cursor: pointer;
  }
  .ebook-shell-root .ebook { max-width: 900px; margin: auto; padding: 0 16px; }
  .ebook-shell-root .page {
    min-height: 1100px;
    padding: 40px 28px;
    margin: 24px auto;
    position: relative;
    overflow: hidden;
    border-radius: 28px;
    background: linear-gradient(135deg, #0b1b2e, #102f24);
    box-shadow: 0 30px 80px rgba(0,0,0,.5);
    page-break-after: always;
  }
  @media (min-width: 640px) { .ebook-shell-root .page { padding: 70px; } }
  .ebook-shell-root .cover {
    background:
      linear-gradient(rgba(0,0,0,.15), rgba(0,0,0,.75)),
      radial-gradient(circle at 70% 20%, rgba(0,212,255,.35), transparent 30%),
      linear-gradient(135deg, #0b3d91, #102f24 55%, #050b14);
  }

  /* Travel variant — mountains + road */
  .ebook-shell-root.variant-travel .mountains {
    position: absolute;
    bottom: 0; left: 0;
    width: 100%; height: 420px;
    background:
      linear-gradient(135deg, transparent 50%, #183b32 51%) bottom left/50% 100% no-repeat,
      linear-gradient(225deg, transparent 50%, #245842 51%) bottom right/65% 95% no-repeat;
    opacity: .85;
    pointer-events: none;
  }
  .ebook-shell-root.variant-travel .road {
    position: absolute;
    bottom: -40px; left: 42%;
    width: 170px; height: 430px;
    background: linear-gradient(#29323c, #050b14);
    transform: perspective(400px) rotateX(55deg);
    border-left: 8px solid rgba(255,255,255,.2);
    border-right: 8px solid rgba(255,255,255,.2);
    pointer-events: none;
  }

  /* Buying variant — dollar-grid perspective floor */
  .ebook-shell-root.variant-buying .dollar-grid {
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 20% 80%, rgba(46,139,87,.18), transparent 22%),
      radial-gradient(circle at 80% 70%, rgba(0,212,255,.18), transparent 20%);
    pointer-events: none;
  }
  .ebook-shell-root.variant-buying .dollar-grid::after {
    content: "";
    position: absolute;
    bottom: 0; left: 0;
    width: 100%; height: 380px;
    background:
      repeating-linear-gradient(90deg, rgba(0,212,255,.08) 0 1px, transparent 1px 80px),
      repeating-linear-gradient(0deg, rgba(0,212,255,.05) 0 1px, transparent 1px 80px);
    transform: perspective(700px) rotateX(58deg);
    transform-origin: center bottom;
    opacity: .8;
  }

  .ebook-shell-root .ai-glow {
    position: absolute;
    right: -120px; top: -120px;
    width: 360px; height: 360px;
    border-radius: 50%;
    background: rgba(0,212,255,.18);
    filter: blur(20px);
    pointer-events: none;
  }
  .ebook-shell-root .brand {
    font-size: 14px;
    letter-spacing: 4px;
    font-weight: bold;
    color: #00d4ff;
    text-transform: uppercase;
  }
  @media (min-width: 640px) { .ebook-shell-root .brand { font-size: 18px; } }
  .ebook-shell-root h1 {
    font-size: 38px;
    line-height: .98;
    margin: 30px 0 16px;
    max-width: 720px;
    position: relative;
    z-index: 2;
  }
  @media (min-width: 640px) { .ebook-shell-root h1 { font-size: 56px; margin: 40px 0 20px; } }
  @media (min-width: 1024px) { .ebook-shell-root h1 { font-size: 68px; line-height: .95; } }
  .ebook-shell-root h2 {
    font-size: 28px;
    margin: 0 0 18px;
    line-height: 1.1;
  }
  @media (min-width: 640px) { .ebook-shell-root h2 { font-size: 36px; margin: 0 0 24px; } }
  @media (min-width: 1024px) { .ebook-shell-root h2 { font-size: 44px; } }
  .ebook-shell-root h3 {
    font-size: 20px;
    color: #00d4ff;
    margin: 0 0 8px;
  }
  @media (min-width: 640px) { .ebook-shell-root h3 { font-size: 24px; } }
  .ebook-shell-root p, .ebook-shell-root li {
    font-size: 16px;
    line-height: 1.55;
    color: rgba(255,255,255,.9);
  }
  .ebook-shell-root ul {
    padding-left: 20px;
    margin: 16px 0;
  }
  .ebook-shell-root ul li { margin-bottom: 12px; }
  @media (min-width: 640px) {
    .ebook-shell-root p, .ebook-shell-root li { font-size: 20px; line-height: 1.55; }
  }
  .ebook-shell-root .subhead {
    font-size: 18px;
    max-width: 650px;
    color: rgba(255,255,255,.9);
    position: relative;
    z-index: 2;
  }
  @media (min-width: 640px) { .ebook-shell-root .subhead { font-size: 22px; } }
  @media (min-width: 1024px) { .ebook-shell-root .subhead { font-size: 26px; } }
  .ebook-shell-root .card-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 28px;
  }
  @media (min-width: 640px) {
    .ebook-shell-root .card-grid { grid-template-columns: repeat(2, 1fr); gap: 22px; margin-top: 36px; }
  }
  .ebook-shell-root .card {
    background: rgba(255,255,255,.1);
    border: 1px solid rgba(255,255,255,.18);
    border-radius: 24px;
    padding: 20px;
    backdrop-filter: blur(10px);
  }
  @media (min-width: 640px) { .ebook-shell-root .card { padding: 24px; } }
  .ebook-shell-root .image-box {
    height: 220px;
    border-radius: 24px;
    margin: 24px 0;
    background:
      linear-gradient(rgba(0,0,0,.1), rgba(0,0,0,.55)),
      radial-gradient(circle at 50% 30%, rgba(255,210,120,.45), transparent 22%),
      linear-gradient(135deg, #1b4965, #2e8b57);
    display: flex;
    align-items: end;
    padding: 22px;
    font-size: 18px;
    font-weight: bold;
  }
  .ebook-shell-root .image-box.buying {
    background:
      linear-gradient(rgba(0,0,0,.15), rgba(0,0,0,.6)),
      radial-gradient(circle at 30% 40%, rgba(0,212,255,.45), transparent 30%),
      linear-gradient(135deg, #0b3d91, #2e8b57);
  }
  .ebook-shell-root .image-box.inspect {
    background:
      linear-gradient(rgba(0,0,0,.2), rgba(0,0,0,.65)),
      radial-gradient(circle at 70% 30%, rgba(255,180,80,.4), transparent 25%),
      linear-gradient(135deg, #2a2a3e, #1b4965);
  }
  @media (min-width: 640px) { .ebook-shell-root .image-box { height: 320px; padding: 30px; font-size: 24px; margin: 30px 0; } }
  .ebook-shell-root .cta {
    background: linear-gradient(135deg, #00d4ff, #2e8b57);
    color: white;
    padding: 16px 24px;
    border-radius: 999px;
    display: inline-block;
    font-size: 18px;
    font-weight: bold;
    margin-top: 24px;
    position: relative;
    z-index: 2;
  }
  @media (min-width: 640px) { .ebook-shell-root .cta { padding: 22px 34px; font-size: 24px; margin-top: 30px; } }
  .ebook-shell-root .footer {
    position: absolute;
    bottom: 22px;
    left: 28px;
    right: 28px;
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: rgba(255,255,255,.55);
    z-index: 3;
  }
  @media (min-width: 640px) {
    .ebook-shell-root .footer { bottom: 35px; left: 70px; right: 70px; font-size: 14px; }
  }
  @media print {
    .ebook-shell-root { background: white; }
    .ebook-shell-root .toolbar { display: none; }
    .ebook-shell-root .page {
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      width: 100%;
      min-height: 100vh;
    }
  }
`;

export default EbookShell;
